import { z } from "zod";
import {
  employmentTypeSchema,
  postingRequirementsSchema,
  salaryTypeSchema,
  venueTypeSchema,
  workerPositionSchema,
} from "./postings";
import { isoDateTimeString, uuidSchema } from "./common";

// Posting template — org-scoped saved draft a user reuses when creating
// a posting. Field set mirrors createPostingRequestSchema minus
// location/expiry (those are situational).

export const postingTemplateUpsertSchema = z.object({
  name: z.string().min(1).max(80),
  position: workerPositionSchema,
  venueType: venueTypeSchema,
  employmentType: employmentTypeSchema,
  title: z.string().min(1).max(120),
  description: z.string().min(1).max(5000),
  salaryMin: z.number().int().nonnegative(),
  salaryMax: z.number().int().nonnegative(),
  salaryType: salaryTypeSchema,
  defaultHeadcount: z.number().int().min(1).max(50),
  benefits: z.array(z.string()).max(20).default([]),
  skillsRequired: z.array(z.string()).max(15).default([]),
  skillsPreferred: z.array(z.string()).max(15).default([]),
  requirements: postingRequirementsSchema.optional(),
  softNotes: z.string().max(500).nullable().optional(),
});

export const postingTemplateSchema = postingTemplateUpsertSchema.extend({
  id: uuidSchema,
  // System templates have no owning org/user.
  orgId: uuidSchema.nullable(),
  createdByUserId: uuidSchema.nullable(),
  isSystem: z.boolean().default(false),
  // Stable slug for system templates (null for org-saved).
  code: z.string().min(1).max(64).nullable().optional(),
  // Short chip label shown in the "Bắt đầu nhanh" picker.
  label: z.string().min(1).max(80).nullable().optional(),
  createdAt: isoDateTimeString,
  updatedAt: isoDateTimeString,
});

export const postingTemplateListResponseSchema = z.object({
  items: z.array(postingTemplateSchema),
});

// Admin-managed system templates. Same payload as org templates plus a
// stable `code` slug and the chip `label` shown on the employer picker.
export const adminSystemPostingTemplateUpsertSchema =
  postingTemplateUpsertSchema.extend({
    code: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[A-Z0-9_]+$/, "Code chỉ chứa chữ in hoa, số và dấu _"),
    label: z.string().min(1).max(80),
  });

export type PostingTemplateUpsertRequest = z.infer<
  typeof postingTemplateUpsertSchema
>;
export type PostingTemplate = z.infer<typeof postingTemplateSchema>;
export type PostingTemplateListResponse = z.infer<
  typeof postingTemplateListResponseSchema
>;
export type AdminSystemPostingTemplateUpsertRequest = z.infer<
  typeof adminSystemPostingTemplateUpsertSchema
>;
